import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { liveAreas } from '../types';
import { AREA_PALETTE as PALETTE } from '../area-colours';
import { displayName } from '../people';
import VendorTag from './VendorTag';
import RenameField from './RenameField';
import ColourField from './ColourField';

/*
 * THE COLOURS ON OFFER, and a fixed list rather than a colour picker — now the
 * shared AREA_PALETTE (src/area-colours.ts), so the Settings swatches and the
 * inline "+ Create area" on a card draw from one list and can never drift.
 *
 * A free picker lets somebody choose two colours nobody can tell apart, or one
 * that vanishes into the panel behind it. These are the board's own status and
 * priority colours, already known to work on this background and to stay apart
 * from each other — which is the whole job of a rail.
 */

/*
 * ONE ROW IS A NAME AND A COLOUR, and everything else waits to be asked for.
 *
 * THIS WAS BUILT THE OTHER WAY FIRST AND IT WAS UNUSABLE. Every control sat on
 * every row — a text box, eight colour swatches, up, down and Remove — so five
 * areas put forty coloured squares and five text boxes on screen at once, and
 * the list you came to read was buried under the controls for changing it.
 * Vinnie, 27 August 2026: "this looks terrible".
 *
 * A row now reads as a line of text. Press Change and that one row becomes the
 * editor, so at most one set of swatches is ever on screen.
 */
export default function AreaSettings() {
  const areas = useApp((s) => s.areas);
  const problem = useApp((s) => s.areasProblem);
  const issues = useApp((s) => s.issues);
  const profiles = useApp((s) => s.profiles);
  const createArea = useApp((s) => s.createArea);
  const editArea = useApp((s) => s.editArea);
  const moveArea = useApp((s) => s.moveArea);
  const retireArea = useApp((s) => s.retireArea);
  const restoreArea = useApp((s) => s.restoreArea);

  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [colour, setColour] = useState(PALETTE[0]);
  const [says, setSays] = useState('');
  const [starting, setStarting] = useState(false);

  // A STARTER SET for an empty board, so the Area picker on a card is never a
  // blank you have to leave and fill from here first — the IT estate most of
  // these boards actually live in. Every one is editable and removable; this is
  // a running start, not a template you are stuck with.
  const addStarterSet = async () => {
    if (starting) return;
    setStarting(true);
    setSays('');
    const STARTER = ['Network', 'Security', 'Servers & Compute', 'End-user Devices', 'Applications', 'Service Desk'];
    for (let i = 0; i < STARTER.length; i++) {
      const res = await createArea({ name: STARTER[i], colour: PALETTE[i % PALETTE.length] });
      if ('error' in res) {
        setSays(res.error);
        break;
      }
    }
    setStarting(false);
  };

  const live = liveAreas(areas);
  const retired = areas.filter((a) => a.retiredAt).sort((a, b) => a.position - b.position);

  /* How many cards each carries — the number that makes Remove a decision. */
  const used = useMemo(() => {
    const n: Record<string, number> = {};
    for (const i of issues) if (i.area) n[i.area] = (n[i.area] ?? 0) + 1;
    return n;
  }, [issues]);

  const run = async (p: Promise<string>) => setSays(await p);
  const cards = (id: string) => (used[id] ? `${used[id]} ${used[id] === 1 ? 'card' : 'cards'}` : 'none yet');

  // Who can be named a lead — the people already working this board (its card
  // assignees), plus anyone currently set as a lead, by display name. A lead is
  // a standing accountability, so the realistic set is the board's own people.
  const people = useMemo(() => {
    const set = new Set<string>();
    for (const i of issues) if (i.assignee) set.add(i.assignee);
    for (const a of areas) if (a.lead) set.add(a.lead);
    return [...set].sort((x, y) => displayName(x, profiles).localeCompare(displayName(y, profiles)));
  }, [issues, areas, profiles]);

  return (
    <section className="panel">
      <h2>The parts of this product</h2>
      <p className="hint">
        What a card is <em>about</em>, as opposed to what sort of work it is. These are yours to
        name: a board that builds software might use UI and Back-end; one that runs a service
        might use neither. An area&rsquo;s colour is the dot on its cards, and colours the lane when
        the board is grouped by area.
      </p>

      {/* A refused read is not an empty list, and must not be drawn as one — #289. */}
      {problem && <p className="form-error">The areas could not be read — {problem}</p>}

      {live.length === 0 && !problem && (
        <div className="area-starter">
          <p className="hint">
            None yet. Until one is added, every card reads &ldquo;No area&rdquo;, which is a real
            answer and not a fault.
          </p>
          <button className="btn btn-small btn-primary" disabled={starting} onClick={() => void addStarterSet()}>
            {starting ? 'Adding…' : 'Add a starter set'}
          </button>
          <span className="area-starter-eg">Network, Security, Servers &amp; Compute, End-user Devices, Applications, Service Desk — edit or remove any.</span>
        </div>
      )}

      <ul className="area-list">
        {live.map((a, at) => (
          <li key={a.id} className={`area-row ${open === a.id ? 'is-open' : ''}`}>
            <div className="area-line">
              <span className="area-dot" style={{ background: a.colour }} />
              <span className="area-name-flat">{a.name}</span>
              {a.lead && <span className="area-lead-flat">Lead: {displayName(a.lead, profiles)}<VendorTag who={a.lead} profiles={profiles} compact /></span>}
              <span className="area-used">{cards(a.id)}</span>
              <button
                className="btn btn-small"
                aria-expanded={open === a.id}
                onClick={() => setOpen(open === a.id ? null : a.id)}
              >
                {open === a.id ? 'Done' : 'Change'}
              </button>
              <button className="btn btn-small" onClick={() => void run(retireArea(a.id))}>
                Remove
              </button>
            </div>

            {open === a.id && (
              <div className="area-editor">
                {/* The same fault was here first and TypeSettings inherited it
                    — one write per keystroke. See RenameField. */}
                <RenameField
                  value={a.name}
                  onRename={(name) => void run(editArea(a.id, { name }))}
                />
                <label className="area-field">
                  Lead
                  <select
                    className="ctl"
                    aria-label={`Lead for ${a.name}`}
                    value={a.lead ?? ''}
                    onChange={(e) => void run(editArea(a.id, { lead: e.target.value }))}
                  >
                    <option value="">No lead set · falls back to the busiest assignee (auto)</option>
                    {people.map((p) => (
                      <option key={p} value={p}>
                        {displayName(p, profiles)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="area-field">
                  Colour
                  <ColourField
                    value={a.colour}
                    onChange={(c) => void run(editArea(a.id, { colour: c }))}
                    label={a.name}
                  />
                </div>
                <div className="area-field">
                  Order
                  <span className="area-moves">
                    <button
                      className="btn btn-small"
                      disabled={at === 0}
                      aria-label={`Move ${a.name} up`}
                      onClick={() => void run(moveArea(a.id, -1))}
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-small"
                      disabled={at === live.length - 1}
                      aria-label={`Move ${a.name} down`}
                      onClick={() => void run(moveArea(a.id, 1))}
                    >
                      ↓
                    </button>
                  </span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <div className="area-editor is-adding">
          <label className="area-field">
            Name
            <input
              className="input"
              value={name}
              autoFocus
              placeholder="UI, Back-end, Reporting…"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="area-field">
            Colour
            <ColourField value={colour} onChange={setColour} label="the new area" />
          </div>
          <div className="area-adds">
            <button
              className="btn btn-small btn-primary"
              disabled={!name.trim()}
              onClick={async () => {
                const res = await createArea({ name, colour });
                setSays('error' in res ? res.error : '');
                if ('id' in res) {
                  setName('');
                  setAdding(false);
                }
              }}
            >
              Add it
            </button>
            <button className="btn btn-small" onClick={() => { setAdding(false); setName(''); setSays(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-small" onClick={() => setAdding(true)}>
          Add an area
        </button>
      )}

      {says && <p className="form-error">{says}</p>}

      {/*
        * WHAT REMOVE DOES, said before it is pressed rather than after.
        *
        * Vinnie decided this on 26 August 2026 over two alternatives: clearing
        * the area off those cards, or refusing until they are moved. Retiring
        * was chosen because the other two rewrite the past — a report drawn in
        * June has to still read the same in December.
        */}
      <p className="hint area-note">
        <b>Removing an area does not clear it off the cards using it.</b> It stops being offered on
        new work and stays where it already is, so last month&rsquo;s figures still add up.
        Anything counting by area keeps counting it and says &ldquo;retired&rdquo; beside it.
        A removed area moves to a Retired list below &mdash; Put back restores it.
      </p>

      {retired.length > 0 && (
        <>
          <h3 className="area-heading">Retired</h3>
          <ul className="area-list">
            {retired.map((a) => (
              <li key={a.id} className="area-row is-retired">
                <div className="area-line">
                  <span className="area-dot" style={{ background: a.colour }} />
                  <span className="area-name-flat">{a.name}</span>
                  <span className="area-used">{cards(a.id)}</span>
                  <button className="btn btn-small" onClick={() => void run(restoreArea(a.id))}>
                    Put back
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

    </section>
  );
}
